// Pure caret-preserving insertion helpers for the compose panel's three content
// surfaces. The container owns the refs/DOM; everything here is side-effect-pure
// except insertIntoQuill, which drives the passed editor instance directly.

// The compose content field a variable insert lands in.
export type InsertTarget = 'subject' | 'bodyHtml' | 'smsBody';

// Which surface a panel insert targets: the last-focused field if the current
// delivery option still renders it, else the visible default (never a hidden one).
export const resolveInsertTarget = (
  remembered: InsertTarget | null,
  { usesEmail, usesSMS }: { usesEmail: boolean; usesSMS: boolean }
): InsertTarget => {
  const rememberedIsVisible = remembered === 'smsBody' ? usesSMS : !!remembered && usesEmail;
  if (rememberedIsVisible) return remembered as InsertTarget;
  return usesEmail ? 'bodyHtml' : 'smsBody';
};

// Structural subset of a Quill editor — lets tests stub the editor and keeps
// this module free of the react-quill-new import graph.
export interface IQuillEditor {
  getLength(): number;
  insertText(index: number, text: string, source: 'user'): void;
  deleteText(index: number, length: number, source: 'user'): void;
  setSelection(index: number, length: number): void;
  focus(): void;
}

// Quill always holds a trailing newline, so an untouched editor has length 1 —
// the max() lands appends before it instead of at a negative index.
export const insertIntoQuill = (
  quill: IQuillEditor,
  token: string,
  lastRange: { index: number; length: number } | null
): void => {
  const index = lastRange?.index ?? Math.max(quill.getLength() - 1, 0);
  // Replace an active selection so a chip click matches typing (a caret is length 0).
  if (lastRange && lastRange.length > 0) quill.deleteText(index, lastRange.length, 'user');
  quill.insertText(index, token, 'user');
  quill.setSelection(index + token.length, 0);
  quill.focus();
};

// Splices the token over the current selection; the caller applies nextValue via
// setFieldValue and restores nextCaret through the input element.
export const insertIntoTextField = (
  value: string,
  selectionStart: number,
  selectionEnd: number,
  token: string
): { nextValue: string; nextCaret: number } => ({
  nextValue: value.slice(0, selectionStart) + token + value.slice(selectionEnd),
  nextCaret: selectionStart + token.length
});

// onMouseDown handler for every variable-panel button so a click never blurs
// the focused content field — the insert still lands at the live caret.
export const keepFieldFocusOnMouseDown = (event: { preventDefault(): void }): void =>
  event.preventDefault();
